<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import { useThemeVars } from 'naive-ui';
import type { Config, Data, Layout } from 'plotly.js';
import { statisticsChartSeriesColors, statisticsChartSurfaceColor } from '@/utils/adminStatistics';

export interface StackedSeries {
  name: string;
  values: number[];
  color?: string;
  offsetGroup?: string;
}

interface Props {
  title: string;
  categories: string[];
  series: StackedSeries[];
  xLabel: string;
  yLabel: string;
  height?: string;
  emptyDescription?: string;
  formatValue?: (value: number) => string;
}

const props = withDefaults(defineProps<Props>(), {
  height: '340px',
  emptyDescription: 'No data',
  formatValue: (value: number) => value.toLocaleString(),
});

// Plotly supports rounded bar ends since v2.25 but @types/plotly.js has no field for it.
type BarLayout = Partial<Layout> & { barcornerradius?: number };

const PlotlyPlot = defineAsyncComponent(() => import('@/components/charts/PlotlyPlot.vue'));
const themeVars = useThemeVars();

const hasData = computed(
  () =>
    props.categories.length > 0 &&
    props.series.some(series => series.values.some(value => value !== 0))
);

const chartData = computed<Data[]>(() =>
  props.series.map((series, index) => {
    const formatted = series.values.map(value => props.formatValue(value));
    return {
      type: 'bar',
      name: series.name,
      x: props.categories,
      y: series.values,
      offsetgroup: series.offsetGroup ?? series.name,
      marker: {
        color:
          series.color ?? statisticsChartSeriesColors[index % statisticsChartSeriesColors.length],
        // Surface-colored hairline so touching stack segments stay distinct.
        line: { color: statisticsChartSurfaceColor, width: 1 },
      },
      customdata: formatted,
      hovertemplate: `%{x}<br>${series.name}: <b>%{customdata}</b><extra></extra>`,
    } as Data;
  })
);

// Past ~12 categories (e.g. one bar per day over 90 days), labelling every
// tick turns the axis into noise — show ~10 evenly spaced labels instead.
// Hover still reveals the exact category of every bar.
const categoryTickStep = computed(() =>
  props.categories.length > 12 ? Math.ceil(props.categories.length / 10) : 1
);

const chartLayout = computed<BarLayout>(() => {
  const gridColor = themeVars.value.dividerColor;
  const mutedText = themeVars.value.textColor3;

  return {
    barmode: 'stack',
    showlegend: true,
    legend: {
      orientation: 'h',
      y: -0.22,
      font: { size: 12, color: themeVars.value.textColor2 },
    },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    bargap: props.categories.length <= 4 ? 0.55 : 0.35,
    barcornerradius: 4,
    font: {
      color: themeVars.value.textColor2,
      family: themeVars.value.fontFamily,
    },
    margin: { t: 8, r: 16, b: 24, l: 8 },
    hoverlabel: {
      font: { family: themeVars.value.fontFamily },
    },
    xaxis: {
      type: 'category',
      automargin: true,
      showgrid: false,
      zeroline: false,
      tick0: 0,
      dtick: categoryTickStep.value,
      tickangle: props.categories.length > 8 ? -35 : 0,
      tickfont: { size: 12, color: mutedText },
    },
    yaxis: {
      title: {
        text: props.yLabel,
        font: { size: 12, color: mutedText },
      },
      automargin: true,
      gridcolor: gridColor,
      zerolinecolor: gridColor,
      tickfont: { size: 12, color: mutedText },
    },
  };
});

const chartConfig = computed<Partial<Config>>(() => ({
  displayModeBar: false,
  scrollZoom: false,
}));
</script>

<template>
  <n-card :title="title" size="small">
    <PlotlyPlot
      v-if="hasData"
      :data="chartData"
      :layout="chartLayout"
      :config="chartConfig"
      :height="height"
    />
    <n-empty v-else :description="emptyDescription" />
  </n-card>
</template>
